export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

export function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate + 'T00:00:00');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = today.getTime() - date.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 14) return `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 9) return `${diffWeeks} weeks ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
}

export function padNumber(value: number, size: number = 2): string {
  let output: string = String(value);
  while (output.length < size) {output = "0" + output;}
  return output;
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp; // Check if current time is past expiration
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return true; // Treat invalid tokens as expired
  }
};

export const isAdminToken = (): boolean => {
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return false;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
    return payload.userType === 'ADMIN';
  } catch (error) {
    return false; // Default to non-admin for invalid tokens
  }
};

export const isUserToken = (): boolean => {
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return false;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
    return payload.userType === 'USER';
  } catch (error) {
    return false; // Default to non-user for invalid tokens
  }
};

export const getPhoneNumberFromToken = (): string => {
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return null;
  }
  const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
  return payload.sub;
};

export const formatStyles = (styles: any) => {
  if (!styles) return '';
  
  const stylesArray = Array.isArray(styles) ? styles : [styles];
  
  return stylesArray
    .map(style => {
      return style
        .replace(/^{/, '') 
        .replace(/}$/, '') 
        .replace(/^"|"$/g, '') 
        .trim();
    })
    .filter(style => style) 
    .join(' • ');
};

/**
 * Formats a UTC time string to a specific timezone
 * @param utcTimeString - Time string in UTC format (e.g., "2025-10-04T18:00:00")
 * @param timeZone - IANA timezone identifier (e.g., "Europe/Tallinn", "America/New_York")
 * @param locale - Locale for formatting (e.g., "et-EE", "en-US")
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted time string in the specified timezone
 */
export const formatToTimezone = (
  utcTimeString: string,
  timeZone: string = 'Europe/Tallinn',
  locale: string = 'et-EE',
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcTimeString) return '';
  
  try {
    // Treat input as UTC by appending 'Z' if not already present
    const utcString = utcTimeString.endsWith('Z') ? utcTimeString : utcTimeString + 'Z';
    const date = new Date(utcString);
    
    const formatOptions: Intl.DateTimeFormatOptions = {
      timeZone,
      ...options
    };
    return date.toLocaleString(locale, formatOptions);
  } catch (error) {
    console.error(`Error formatting time to ${timeZone} timezone:`, error);
    return utcTimeString; // Return original string if formatting fails
  }
};


 export const interpretAsTallinnTime = (localDate: Date): Date => {
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  const seconds = String(localDate.getSeconds()).padStart(2, '0');
  const ms = String(localDate.getMilliseconds()).padStart(3, '0');
  
  // Determine if this date would be in DST in Tallinn
  // DST typically runs from last Sunday in March to last Sunday in October
  const monthNum = localDate.getMonth() + 1;
  const isDST = monthNum > 3 && monthNum < 10; // Simplified: Apr-Sep is DST
  
  const offset = isDST ? '+03:00' : '+02:00';
  const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${offset}`;
  
  return new Date(isoString);
}